/**
 * DP FMEA LAB — DP101 chapter 7. The document that turns a claim into an audited property.
 *
 * ── THE CHAPTER'S VERDICT IS THE HARSHEST IN THE COURSE, SO IT IS THE HEADLINE ────────────────
 * §5: "A generic claim of 'DP2' with an unproven or stale FMEA is a hull with two switchboards, not
 * an equipment class." Chapters 2 and 3 taught what the class means and chapter 6 what the envelope
 * is. This one says none of it counts as a property of a vessel until somebody has enumerated the
 * failure modes and then physically injected them.
 *
 * ── SO THE LAB WALKS HER OWN ENUMERATION AND MARKS WHAT IS UNPROVEN ───────────────────────────
 * Every single failure the model can express is listed against the element it belongs to, and the
 * computed consequence is shown beside it. That is the ANALYSIS half, and it is genuinely computed.
 *
 * The TRIALS half is not, and the lab says so in the loudest way available: every row is marked
 * NOT PROVEN, because nothing here opened a breaker or blacked out a switchboard. A bench that
 * quietly implied otherwise would be committing the exact fault §5 condemns — a claim resting on an
 * analysis nobody injected. §6 turns the same discipline on this Academy, so applying it to the
 * bench that teaches it is the chapter's own instruction rather than modesty.
 */
import { useState, useEffect } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  FMEA_ENUMERATES, FMEA_ELEMENTS, TRIALS_INJECT, TRIALS_ACTS, DP2_WITHOUT_IT, SOURCE_CH7,
  assemble, activeFailures, threeQuestions, criterionOf, WHY_NOT_SCORED, loadCore,
} from './dplabs.mjs';

/* Which declared element each failure family belongs to. The families are the model's; the element
   names are the chapter's list, so a row can be traced to the document it would sit in. */
const ELEMENT_OF = { generator: 'generation', switchboard: 'distribution', thruster: 'thrusters' };

export default function DpFmeaLab() {
  const [model, setModel] = useState(null);
  const [core, setCore] = useState(null);
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let live = true;
    Promise.all([fetch('/api/dp/model').then((r) => r.json()), loadCore()])
      .then(([m, c]) => { if (live) { setModel(m); setCore(c); } })
      .catch((e) => live && setErr(String(e)));
    return () => { live = false; };
  }, []);

  if (err) return <div className="g-glass shell-panel lab"><p className="g-body"><Value of={refused(`Her model and the engine could not be read: ${err}. No enumeration is shown rather than a partial one.`)} /></p></div>;
  if (!model || !core) return <div className="g-glass shell-panel lab"><p className="g-body g-dim">Loading her declared model and the engine…</p></div>;

  const built = assemble(model, [...(model.thrusters || []).map((t) => t.id), ...(model.generators || []).map((g) => g.id)]);
  const rows = activeFailures(built).map((c) => ({ ...c, element: ELEMENT_OF[c.kind] || c.kind }));
  const crit = criterionOf(model);
  /* Covered elements are those her enumeration reaches. The three it does not are named rather than
     omitted — an FMEA's gaps are the part a reader must be told about. */
  const covered = new Set(rows.map((r) => r.element));

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">FMEA bench · analysis is not proof</div>
      <h2 className="g-h2 lab-h">A hull with two switchboards, or an equipment class?</h2>

      <p className="g-body"><Value of={cited(DP2_WITHOUT_IT, SOURCE_CH7)} /></p>
      <p className="g-body g-dim">
        The FMEA <Value of={cited(FMEA_ENUMERATES, SOURCE_CH7)} />: {FMEA_ELEMENTS.join(', ')} — and their cross-connections.
      </p>

      {/* ── WHAT HER ENUMERATION REACHES, AND WHAT IT DOES NOT ── */}
      <div className="lab-task">
        <div className="g-label">Which elements this model can enumerate</div>
        <div className="dpc-parts">
          {FMEA_ELEMENTS.map((e) => (
            <div key={e} className="dpc-part">
              {covered.has(e) ? <Value of={computed(e)} /> : <Value of={absent()} />}
              <span className="g-dim"> {covered.has(e) ? `${rows.filter((r) => r.element === e).length} failure mode(s)` : 'not declared in this model'}</span>
            </div>
          ))}
        </div>
        <p className="g-body g-dim">
          The elements marked absent are not claimed as clean — they are outside what her model
          declares, and a real FMEA covers them. Saying so is the difference between an enumeration
          and a comforting one.
        </p>
      </div>

      {/* ── THE ANALYSIS, COMPUTED ── */}
      <div className="lab-task">
        <div className="g-label">Every single failure this model can express, with its computed effect</div>
        <table className="dpb-table">
          <tbody>
            {rows.map((r) => {
              const shown = open === r.id;
              const q = shown ? threeQuestions(built, core, model, r.dead, r.deadGenerators) : null;
              return (
                <tr key={r.id} className={shown ? 'dpb-verdict' : ''} onClick={() => setOpen(shown ? null : r.id)}>
                  <td><span className="g-dim">{r.element}</span><br />{r.label}</td>
                  <td className="dpb-num">
                    {q
                      ? <Value of={computed(`${q.best_max_wind_kn} kn at ${q.best_heading_deg}° → ${q.worst_max_wind_kn} kn at ${q.worst_heading_deg}°`)} />
                      : <span className="g-dim">open</span>}
                  </td>
                  <td>
                    {q && (q.retains_control
                      ? <Value of={computed('retains control')} />
                      : <Value of={refused('loses control')} />)}
                    <div><Value of={refused('NOT PROVEN — no trial injected')} /></div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── THE HALF THIS BENCH CANNOT DO ── */}
      <div className="lab-task dpb-verdict">
        <div className="g-label">What this bench is not</div>
        <p className="g-body">
          An analysis on paper is an analysis on paper. Its conclusions are{' '}
          <Value of={cited(TRIALS_INJECT, SOURCE_CH7)} />: {TRIALS_ACTS.join(', ')}.
        </p>
        <p className="g-body">
          <Value of={refused('Nothing on this bench has been injected. Every row above is analysis, computed against her declared criterion of ' + crit.wind_kn + ' kn — and every one is marked NOT PROVEN for that reason.')} />
        </p>
        <p className="g-body g-dim">
          Chapter 7 turns the same discipline on this Academy, so it is turned on this bench too. A
          simulation that let its rows read as trials would be the fault the chapter names, committed
          by the instrument built to teach it.
        </p>
      </div>

      <p className="g-body g-dim lab-foot">{WHY_NOT_SCORED}</p>
      <p className="g-body g-dim"><Value of={absent()} /> Nothing here is an attestation, and no output of this bench is evidence toward one.</p>
    </div>
  );
}
